import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function PaymentsPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Payments & Payouts Disclosure
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.payments}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-blue-900 mb-2">Important Notice</h3>
            <p className="text-blue-800">
              Southwest Florida Vacation Rentals is a <strong>marketplace platform</strong>, 
              not a bank, escrow agent, or payment processor. All payments are processed by 
              Stripe, a third-party payment service provider.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. How Payments Work
            </h2>
            <h3 className="font-semibold text-slate-800 mb-2">For Guests</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Guests pay the total booking amount (nightly rate + cleaning fee + service fee) 
                  at the time of booking confirmation</li>
              <li>Payment is collected by Stripe on behalf of SWFL Rentals</li>
              <li>Accepted payment methods: Credit cards, debit cards</li>
              <li>All transactions are secured with industry-standard encryption</li>
            </ul>
            
            <h3 className="font-semibold text-slate-800 mb-2">For Hosts</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Hosts receive payouts via Stripe Connect</li>
              <li>Payouts are initiated 24 hours after guest check-in</li>
              <li>Standard payout processing time: 2-7 business days depending on your bank</li>
              <li>Hosts must complete Stripe Connect onboarding to receive payouts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Fee Structure
            </h2>
            <div className="bg-slate-50 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-slate-800 mb-3">Guest Service Fee: 6%</h3>
              <p className="text-slate-600 mb-4">
                A service fee of 6% of the booking subtotal (nightly rate + cleaning fee) 
                is charged to guests. This fee supports platform operations, customer support, 
                and secure payment processing.
              </p>
              
              <h3 className="font-semibold text-slate-800 mb-3">Host Service Fee: 3%</h3>
              <p className="text-slate-600">
                A service fee of 3% is deducted from the Host's payout. This fee covers 
                payment processing, platform maintenance, and support services.
              </p>
            </div>
            <p className="text-slate-600">
              <strong>Total Platform Fee: 9%</strong> (6% from Guest + 3% from Host)
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. Payment Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Booking Confirmed</h4>
                  <p className="text-slate-600">Guest's payment is collected by Stripe</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Funds Held</h4>
                  <p className="text-slate-600">Stripe holds the funds until check-in</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Payout Initiated</h4>
                  <p className="text-slate-600">24 hours after check-in, payout is initiated to Host</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Payout Received</h4>
                  <p className="text-slate-600">Host receives funds (2-7 business days)</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Stripe Connect for Hosts
            </h2>
            <p className="text-slate-600 mb-4">
              To receive payouts, Hosts must create a Stripe Connect Express account. This process:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Verifies your identity for fraud prevention</li>
              <li>Collects banking information for direct deposits</li>
              <li>Ensures compliance with financial regulations</li>
              <li>Is managed entirely by Stripe (not SWFL Rentals)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Platform Role Clarification
            </h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <p className="text-amber-900 mb-4">
                <strong>SWFL Rentals is NOT:</strong>
              </p>
              <ul className="list-disc pl-6 text-amber-800 space-y-2">
                <li>A bank or financial institution</li>
                <li>An escrow agent or holding account</li>
                <li>A payment processor (Stripe handles all payment processing)</li>
                <li>Responsible for holding, investing, or managing guest funds</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Currency & Taxes
            </h2>
            <p className="text-slate-600 mb-4">
              All transactions on our platform are processed in US Dollars (USD). 
              Hosts are responsible for reporting income and paying applicable taxes. 
              We may provide tax documentation (e.g., 1099-K) as required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Disputes & Chargebacks
            </h2>
            <p className="text-slate-600 mb-4">
              In case of payment disputes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Contact our support team first for resolution</li>
              <li>We will facilitate communication between parties</li>
              <li>Stripe's dispute resolution process will be followed if needed</li>
              <li>Chargebacks may result in temporary holds on Host payouts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Contact
            </h2>
            <p className="text-slate-600">
              For payment-related questions, please contact us at:<br />
              Email: payments@southwestflvacationrentals.com
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
